// Read endpoints (devices, tests). Uses fetch directly with credentials so
// the Better-Auth session cookie is sent. Could swap to hc<App> for full
// end-to-end types once we re-export the App type cleanly across the
// workspace boundary; for now keep it simple with hand-typed responses.

export type Device = {
  id: string;
  orgId: string;
  serial: string;
  // Warranty / asset serial (BT1851-XXXX-XXXX). The user-facing identity —
  // stable across CM5 + board swaps. Null for legacy / un-provisioned units;
  // fall back to the CM5 OTP `serial` for display. Already grouped 4-4, so it
  // is shown verbatim (no formatSerial — that's hex-specific to the OTP serial).
  assetSerial: string | null;
  name: string | null;
  pairedAt: string;
  lastSeen: string | null;
  firmwareVersion: string | null;
  updateChannel: "stable" | "beta";
};

// Commissioning/warranty asset (COMMISSIONING_WARRANTY.md). One per physical unit,
// keyed on the maker-assigned asset_serial; persists across CM5/board swaps.
export type Asset = {
  assetSerial: string;
  serial: string | null;
  ekHash: string | null;
  commissionedAt: string | null;
  soldAt: string | null;
  soldByOrgId: string | null;
  soldToContact: string | null;
  warrantyStart: string | null;
  warrantyMonths: number;
  warrantyExpires: string | null;
  status: string;            // in_stock | sold | deployed | decommissioned | void
  orgId: string;
  deviceId: string | null;
  plan: string | null;
  notes: string | null;
  qaPassed?: boolean;        // reseller/stock only: commission-finalize wrote qa_passed
};

export type WarrantyState = "not_started" | "in_warranty" | "expired" | "void";
export type WarrantyInfo = {
  state: WarrantyState;
  covered: boolean;
  expires_at: string | null;
  asset_status?: string;
  warranty_start?: string | null;
};
export type AssetEvent = {
  id: number;
  assetSerial: string;
  ts: string;
  type: string;
  deviceId: string | null;
  ekHash: string | null;
  actor: string | null;
  detail: string | null;
  notes: string | null;
};

export type TestSummary = {
  id: string;
  deviceId: string;
  // User-set device label (devices.name). Null until the user renames the
  // device. Joined onto the test list/detail responses so the UI can show
  // a human name instead of the opaque cloud device id.
  deviceName: string | null;
  deviceTestId: number;
  testType: number;
  testResult: number;
  // User-set label. Captured on the device before test start, then frozen
  // there once synced; renames after the first sync happen cloud-side via
  // PATCH /v1/orgs/:org/tests/:id (api.renameTest).
  name: string | null;
  startedAt: string;
  durationMs: number;
  attestationStatus: string;
};

// Client-visible TestDetail. Crucially does NOT include R2 keys — those
// are internal storage paths that should never reach the browser. The UI
// just needs to know whether the PDF report exists (`hasPdf`); the
// time-series chart loads via the dedicated endpoint.
export type TestDetail = TestSummary & {
  orgId: string;
  paramsJson: string;
  hasPdf: boolean;
  // Combined (Master–Slave 2×) tag. 0/undefined = normal single-unit;
  // 1 = combined master (samples carry the TOTAL current of both units).
  combinedRole?: number;
  peerSerial?: string | null;
};

// Staff-visible TestDetail. Carries the full row from D1, including the
// R2 keys (which staff legitimately need to know exist). Mirrors the
// shape `handleStaffTestDetail` returns from `select().from(tests)`.
export type StaffTestDetail = TestSummary & {
  orgId: string;
  paramsJson: string;
  dataBlobKey: string | null;
  chartBlobKey: string | null;
  pdfBlobKey: string | null;
  updatedAt?: string;
};


export interface BatteryMakerRow {
  id: string;
  maker: string;
  model: string | null;
  status: string;
  alias_of: string | null;
  context: string | null;
  capacity_ah: number | null;
  capacity_rate_h: number | null;   // rate capacity_ah is quoted at (hours: 20 = C20, 10 = C10); null = unknown
  afp: string | null;
  chemistry: string | null;
  datasheet_url: string | null;
  updated_at: number;
}

async function http<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: "include", ...(init ?? {}) });
  if (r.status === 401 && !location.pathname.startsWith("/login")) {
    // The session died server-side (expiry, staff eviction, account block).
    // useSession() keeps serving its cached logged-in state, so the route
    // guards never redirect and the user is stranded on a dead screen until
    // they reload by hand. Hard-navigate instead: the full page load resets
    // the Better-Auth store, lands on the login page, and the `next`
    // round-trip brings the user back here after re-authenticating.
    const next = location.pathname + location.search;
    window.location.assign(`/login?next=${encodeURIComponent(next)}`);
    // Navigation is in flight — never settle, so no error state flashes.
    return new Promise<T>(() => {});
  }
  if (!r.ok) {
    // Surface the server's `error` field so the UI shows actionable copy
    // ("must-block-first") instead of bare HTTP status. Body parsing is
    // best-effort — non-JSON responses still get the status fallback.
    let detail = `${r.status} ${r.statusText}`;
    try {
      const body = await r.json() as { error?: string; detail?: string };
      if (body.error) detail = body.detail ? `${body.error}: ${body.detail}` : body.error;
    } catch {
      // ignore
    }
    throw new Error(detail);
  }
  return r.json() as Promise<T>;
}

// Time-series row shape — superset of the fields the device may emit.
// Every column except data_id is optional; the device skips rows for
// fields that were null at the moment.
// Per-test-type chart projection — the only sample shape clients receive.
// `t` is seconds since test start (server already subtracts t0). `v` is
// battery voltage, always present; `i` (shunt current) is included only
// for CPD. Mirrors apps/api/src/charts.ts.
export type ChartPoint = {
  t: number;
  v: number;
  i?: number;
};

// Full sample row — staff-only. Clients never see this shape.
export type DataPoint = {
  data_id?: number;
  data_stamp?: number;        // unix epoch seconds
  battery_voltage?: number;
  fuse_voltage?: number;
  shunt_current?: number;
  set_current?: number;
  self_check_voltage?: number;
  ext_voltage?: number;
  ext_current?: number;
  ext_temp_ext?: number;
  ext_temp_int?: number;
  fcb1_voltage?: number;
  fcb1_current?: number;
  fcb1_power?: number;
  fcb1_pwm?: number;
  fcb1_max_temp?: number;
  fcb2_voltage?: number;
  fcb2_current?: number;
  fcb2_power?: number;
  fcb2_pwm?: number;
  fcb2_max_temp?: number;
  r1start_temp?: number;
  r1end_temp?: number;
  r2start_temp?: number;
  r2end_temp?: number;
  ccb_temp_1_1?: number;
  ccb_temp_1_2?: number;
  ccb_temp_1_3?: number;
  ccb_temp_1_4?: number;
  ccb_temp_1_5?: number;
  ccb_temp_1_6?: number;
  ccb_temp_1_7?: number;
  ccb_temp_1_8?: number;
  ccb_temp_2_1?: number;
  ccb_temp_2_2?: number;
  ccb_temp_2_3?: number;
  ccb_temp_2_4?: number;
  ccb_temp_2_5?: number;
  ccb_temp_2_6?: number;
  ccb_temp_2_7?: number;
  ccb_temp_2_8?: number;
};

// Maker-staff portal (cross-tenant). Gated server-side by `c.staffRole` —
// a non-staff fetcher gets 403, so it's safe to call these speculatively.
export type StaffRole = "support" | "admin" | "developer";

export type StaffMe = {
  isStaff: boolean;
  role: StaffRole | null;
  // Whether the user belongs to any client org — drives the staff nav's
  // "Client portal" link (disabled when false, since "/" would bounce
  // back to /staff).
  hasOrgMembership: boolean;
};

export type StaffOrg = {
  id:            string;
  name:          string;
  slug:          string | null;
  createdAt:     string;
  deviceCount:   number;
  testCount:     number;
  blockedAt:     string | null;
  blockedReason: string | null;
};

export type OrgRole = "owner" | "admin" | "member";

export type OrgMember = {
  id:            string;
  userId:        string;
  role:          string;
  createdAt:     string;
  blockedAt:     string | null;
  blockedReason: string | null;
  email:         string | null;
  name:          string | null;
};

export type AccountMembership = {
  orgId:   string;
  orgName: string | null;
  role:    string;          // owner | admin | member
};

export type StaffAccount = {
  id:            string;
  email:         string;
  name:          string | null;
  createdAt:     string;
  lastSeenAt:    string | null;
  blockedAt:     string | null;
  blockedReason: string | null;
  memberships:   AccountMembership[];
  staffRole:     StaffRole | null;
};

export type StaffUserRow = {
  id:        string;
  userId:    string;
  email:     string | null;
  name:      string | null;
  role:      StaffRole;
  createdAt: string;
  createdBy: string | null;
};

// Infineon EK intermediate CA the enroll path trusts. `pinned` = compiled-in
// (CA071/066); `auto` = learned at enroll (chained to Root CA 2). `blocked`
// overrides trust.
export type AttestCaRow = {
  fingerprint:     string;
  issuerCn:        string;
  source:          "pinned" | "auto";
  blocked:         boolean;
  firstSeenAt:     string | null;
  firstSeenDevice: string | null;
};

export const api = {
  listDevices: (orgId: string) =>
    http<{ devices: Device[] }>(`/v1/orgs/${orgId}/devices`),

  deviceDetail: (orgId: string, deviceId: string) =>
    http<{ device: Device; warranty: WarrantyInfo | null }>(`/v1/orgs/${orgId}/devices/${deviceId}`),

  updateDevice: (orgId: string, deviceId: string, name: string) =>
    http<{ status: "ok"; name: string }>(
      `/v1/orgs/${orgId}/devices/${deviceId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      },
    ),

  listTests: (orgId: string, limit = 100, deviceFilter?: string) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (deviceFilter) q.set("device", deviceFilter);
    return http<{ tests: TestSummary[] }>(`/v1/orgs/${orgId}/tests?${q}`);
  },

  testDetail: (orgId: string, testId: string) =>
    http<{ test: TestDetail }>(`/v1/orgs/${orgId}/tests/${testId}`),

  renameTest: (orgId: string, testId: string, name: string) =>
    http<{ status: "ok"; name: string }>(`/v1/orgs/${orgId}/tests/${testId}`, {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ name }),
    }),

  // Client time-series fetch — returns the per-test-type chart projection,
  // not the full samples. The matching server handler lives in web.ts.
  testData: (orgId: string, testId: string) =>
    http<ChartPoint[]>(`/v1/orgs/${orgId}/tests/${testId}/data`),

  testPdfUrl: (orgId: string, testId: string) =>
    `/v1/orgs/${orgId}/tests/${testId}/pdf`,

  // Org-owner / org-admin member management
  listMembers: (orgId: string) =>
    http<{ members: OrgMember[] }>(`/v1/orgs/${orgId}/members`),

  blockMember: (orgId: string, memberId: string, reason: string | null) =>
    http<{ status: "ok" }>(`/v1/orgs/${orgId}/members/${memberId}/block`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    }),

  unblockMember: (orgId: string, memberId: string) =>
    http<{ status: "ok" }>(`/v1/orgs/${orgId}/members/${memberId}/unblock`, {
      method: "POST",
    }),

  // ---- Commissioning / warranty (reseller portal, P5) --------------------
  myOrgs: () =>
    http<{ orgs: { id: string; name: string; blocked: boolean }[] }>("/v1/me/orgs"),

  orgInfo: (orgId: string) =>
    http<{ type: string; name: string; slug: string | null }>(`/v1/orgs/${orgId}`),

  resellerStock: (orgId: string) =>
    http<{ assets: Asset[] }>(`/v1/orgs/${orgId}/reseller/stock`),

  resellerSell: (
    orgId: string,
    assetSerial: string,
    body: { sold_at?: string; sold_to_contact?: string; customer_org_id?: string },
  ) =>
    http<{ status: "ok"; asset_serial: string; sold_at: string; warranty_expires: string }>(
      `/v1/orgs/${orgId}/reseller/assets/${assetSerial}/sell`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
    ),

  resellerTransferTargets: (orgId: string) =>
    http<{ orgs: { id: string; name: string }[] }>(`/v1/orgs/${orgId}/reseller/transfer-targets`),

  resellerTransfer: (orgId: string, assetSerial: string, body: { to_org_id: string }) =>
    http<{ status: "ok"; asset_serial: string; transferred_to: string }>(
      `/v1/orgs/${orgId}/reseller/assets/${assetSerial}/transfer`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
    ),

  // ---- Staff portal ------------------------------------------------------
  staffMe: () => http<StaffMe>("/v1/staff/me"),

  staffListOrgs: () =>
    http<{ orgs: StaffOrg[] }>("/v1/staff/orgs"),

  staffListOrgMembers: (orgId: string) =>
    http<{ members: OrgMember[] }>(`/v1/staff/orgs/${orgId}/members`),

  staffListDevices: (orgId: string) =>
    http<{ devices: Device[] }>(`/v1/staff/orgs/${orgId}/devices`),

  staffListTests: (orgId: string, limit = 100, deviceFilter?: string) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (deviceFilter) q.set("device", deviceFilter);
    return http<{ tests: TestSummary[] }>(`/v1/staff/orgs/${orgId}/tests?${q}`);
  },

  staffTestDetail: (orgId: string, testId: string) =>
    http<{ test: StaffTestDetail }>(`/v1/staff/orgs/${orgId}/tests/${testId}`),

  staffTestData: (orgId: string, testId: string) =>
    http<DataPoint[]>(`/v1/staff/orgs/${orgId}/tests/${testId}/data`),

  staffTestPdfUrl: (orgId: string, testId: string) =>
    `/v1/staff/orgs/${orgId}/tests/${testId}/pdf`,

  staffDeviceLookup: (deviceId: string) =>
    http<{ device: Device }>(`/v1/staff/devices/${deviceId}`),

  // Asset / warranty console (P5.2)
  staffAssetCard: (assetSerial: string) =>
    http<{ asset: Asset; device: Device | null; events: AssetEvent[]; warranty: WarrantyInfo }>(
      `/v1/staff/assets/${assetSerial}`),
  staffDeviceRmaAuthorize: (orgId: string, deviceId: string) =>
    http<{ status: "ok"; rma_authorized: true }>(
      `/v1/staff/orgs/${orgId}/devices/${deviceId}/rma-authorize`, { method: "POST" }),
  staffDeviceDecommission: (orgId: string, deviceId: string) =>
    http<{ status: "ok"; retired: true }>(
      `/v1/staff/orgs/${orgId}/devices/${deviceId}/decommission`, { method: "POST" }),
  staffDeviceSetAssetSerial: (orgId: string, deviceId: string, assetSerial: string) =>
    http<{ status: "ok"; asset_serial: string }>(
      `/v1/staff/orgs/${orgId}/devices/${deviceId}/asset-serial`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ asset_serial: assetSerial }) }),
  staffVoidWarranty: (assetSerial: string, body: { void: boolean; reason?: string }) =>
    http<{ status: "ok"; asset_status: string }>(
      `/v1/staff/assets/${assetSerial}/void`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),

  staffAssetsList: () =>
    http<{ assets: (Asset & { orgName: string | null; qaPassed: boolean; warranty: string })[] }>(
      "/v1/staff/assets"),

  staffAssetQaStatus: (assetSerial: string) =>
    http<{
      asset_serial: string; status: string; paired: boolean; device_id?: string;
      enrolled: boolean; attested_fresh: boolean; asset_serial_match: boolean;
      results_synced: boolean; ready: boolean;
    }>(`/v1/staff/assets/${assetSerial}/qa-status`),

  staffAssetFinalize: (assetSerial: string, body: { force?: boolean } = {}) =>
    http<{ status: "ok"; asset_serial: string; finalized: boolean; qa_device_retired: string }>(
      `/v1/staff/assets/${assetSerial}/finalize`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),

  staffDeviceServiceMode: (orgId: string, deviceId: string, enable: boolean) =>
    http<{ status: "ok"; service_mode: boolean }>(
      `/v1/staff/orgs/${orgId}/devices/${deviceId}/service-mode`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ enable }) }),

  staffAssetRepairNote: (assetSerial: string, note: string) =>
    http<{ status: "ok" }>(
      `/v1/staff/assets/${assetSerial}/repair-note`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ note }) }),

  staffAssetReplace: (assetSerial: string, replacementAssetSerial: string) =>
    http<{ status: "ok"; replaced_by: string }>(
      `/v1/staff/assets/${assetSerial}/replace`,
      { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ replacement_asset_serial: replacementAssetSerial }) }),

  staffListUsers: () =>
    http<{ staff: StaffUserRow[] }>("/v1/staff/users"),

  staffCreateUser: (email: string, role: StaffRole) =>
    http<{ status: "ok"; userId: string; role: StaffRole }>(
      "/v1/staff/users",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, role }),
      },
    ),

  staffDeleteUser: (id: string) =>
    http<{ status: "ok" }>(`/v1/staff/users/${id}`, { method: "DELETE" }),

  // Attestation trust anchors (H3a)
  // ---- battery-maker catalog curation (§0.4) ----
  staffListBatteryMakers: () =>
    http<{ makers: BatteryMakerRow[] }>("/v1/staff/battery-makers"),
  staffUpsertBatteryMaker: (body: Partial<BatteryMakerRow> & { id?: string }) =>
    http<{ id: string; updated_at: number }>("/v1/staff/battery-makers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  staffDeleteBatteryMaker: (id: string) =>
    http<{ deleted: true }>("/v1/staff/battery-makers/delete", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    }),

  staffListAttestCas: () =>
    http<{ cas: AttestCaRow[] }>("/v1/staff/attest/cas"),
  staffBlockAttestCa: (fingerprint: string) =>
    http<{ blocked: true; fingerprint: string; issuerCn: string }>(
      `/v1/staff/attest/cas/${fingerprint}/block`, { method: "POST" }),
  staffUnblockAttestCa: (fingerprint: string) =>
    http<{ blocked: false; fingerprint: string }>(
      `/v1/staff/attest/cas/${fingerprint}/unblock`, { method: "POST" }),

  // Org block/unblock/delete
  staffBlockOrg: (orgId: string, reason: string | null) =>
    http<{ status: "ok" }>(`/v1/staff/orgs/${orgId}/block`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    }),
  staffUnblockOrg: (orgId: string) =>
    http<{ status: "ok" }>(`/v1/staff/orgs/${orgId}/unblock`, { method: "POST" }),
  staffDeleteOrg: (orgId: string) =>
    http<{ status: "ok"; deleted: { tests: number; devices: number; orgId: string } }>(
      `/v1/staff/orgs/${orgId}`,
      { method: "DELETE" },
    ),

  // User-account block/unblock/delete + listing
  staffListAccounts: () =>
    http<{ accounts: StaffAccount[] }>("/v1/staff/accounts"),
  staffBlockAccount: (userId: string, reason: string | null) =>
    http<{ status: "ok" }>(`/v1/staff/accounts/${userId}/block`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    }),
  staffUnblockAccount: (userId: string) =>
    http<{ status: "ok" }>(`/v1/staff/accounts/${userId}/unblock`, { method: "POST" }),
  staffDeleteAccount: (userId: string) =>
    http<{ status: "ok" }>(`/v1/staff/accounts/${userId}`, { method: "DELETE" }),

  // Org creation
  staffCreateOrg: (name: string, slug: string | null) =>
    http<{ status: "ok"; id: string; name: string; slug: string }>(
      "/v1/staff/orgs",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, slug }),
      },
    ),

  staffRenameOrg: (orgId: string, name: string) =>
    http<{ status: "ok"; id: string; name: string }>(
      `/v1/staff/orgs/${orgId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      },
    ),

  // Account creation (admin-driven, two modes)
  staffCreateAccount: (input: {
    email: string;
    name?: string;
    mode: "password" | "invite";
    password?: string;
  }) =>
    http<{ status: "ok"; userId: string | null; mode: "password" | "invite"; email: string }>(
      "/v1/staff/accounts",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      },
    ),

  staffResetPassword: (userId: string) =>
    http<{ status: "ok"; email: string }>(
      `/v1/staff/accounts/${userId}/reset-password`,
      { method: "POST" },
    ),

  staffAddMembership: (userId: string, orgId: string, role: OrgRole) =>
    http<{ status: "ok"; id: string }>(
      `/v1/staff/accounts/${userId}/memberships`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgId, role }),
      },
    ),

  staffRemoveMembership: (userId: string, orgId: string) =>
    http<{ status: "ok" }>(
      `/v1/staff/accounts/${userId}/memberships/${orgId}`,
      { method: "DELETE" },
    ),

  // ---- OAuth clients (cloud = OIDC provider) -----------------------------
  // The cloud signs in staff-side tools like Grafana, the local staff portal,
  // etc. via OIDC. List/edit/delete go through our staff endpoints; create
  // goes through Better-Auth's /oauth2/register endpoint (gated to admin in
  // index.ts) because it owns the client-secret hashing.
  staffListOAuthClients: () =>
    http<{ clients: OAuthClient[] }>("/v1/staff/oauth-clients"),

  staffCreateOAuthClient: (name: string, redirectUris: string[]) =>
    http<OAuthClientCreated>("/v1/auth/oauth2/register", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({
        client_name:                 name,
        redirect_uris:               redirectUris,
        grant_types:                 ["authorization_code", "refresh_token"],
        response_types:              ["code"],
        token_endpoint_auth_method:  "client_secret_basic",
      }),
    }),

  staffUpdateOAuthClient: (
    id: string,
    patch: { name?: string; redirectUrls?: string[]; disabled?: boolean },
  ) =>
    http<{ status: "ok" }>(`/v1/staff/oauth-clients/${id}`, {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify(patch),
    }),

  staffDeleteOAuthClient: (id: string) =>
    http<{ status: "ok" }>(`/v1/staff/oauth-clients/${id}`, { method: "DELETE" }),

  // ---- Audit log (admin-only forensic view) ------------------------------
  // Cursor-paginated by descending timestamp. `before` is the cursor — pass
  // the previous response's `nextBefore` to fetch the next page. All
  // filters are optional. The backend caps `limit` to 500.
  // ---- Backups (admin-only) ----------------------------------------------
  // List backups/d1/* in R2 + manual trigger of the daily cron. The
  // cron itself is registered in wrangler.toml [triggers]; this lets
  // an admin force a snapshot for the restore-drill workflow or to
  // catch-up immediately after a fresh deploy.
  staffListBackups: () =>
    http<{ backups: BackupSummary[]; truncated: boolean }>("/v1/staff/backups"),

  staffRunBackup: () =>
    http<{ status: "ok"; key: string; monthlyKey: string | null; rows: number; bytes: number }>(
      "/v1/staff/backups/run",
      { method: "POST" },
    ),

  staffAuditLog: (params: {
    limit?:      number;
    before?:     number;
    sinceMs?:    number;
    actor?:      string;
    targetType?: string;
    targetId?:   string;
    success?:    boolean;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.limit      != null) qs.set("limit",      String(params.limit));
    if (params.before     != null) qs.set("before",     String(params.before));
    if (params.sinceMs    != null) qs.set("sinceMs",    String(params.sinceMs));
    if (params.actor)              qs.set("actor",      params.actor);
    if (params.targetType)         qs.set("targetType", params.targetType);
    if (params.targetId)           qs.set("targetId",   params.targetId);
    if (params.success !== undefined) qs.set("success", params.success ? "true" : "false");
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return http<{
      entries:    AuditLogEntry[];
      nextBefore: number | null;
      hasMore:    boolean;
    }>(`/v1/staff/audit-log${suffix}`);
  },
};

export type BackupSummary = {
  key:        string;       // backups/d1/YYYY-MM-DD.json.gz
  size:       number;
  uploaded:   string;       // ISO timestamp
  exportedAt: string | null;
  durationMs: number | null;
};

export type AuditLogEntry = {
  id:            string;
  timestamp:     number;       // ms epoch
  actorUserId:   string;
  actorEmail:    string;
  staffRole:     "support" | "admin" | "developer";
  action:        string;
  targetType:    string | null;
  targetId:      string | null;
  targetSummary: unknown;      // parsed JSON or null
  requestId:     string | null;
  ip:            string | null;
  userAgent:     string | null;
  success:       boolean;
  failureReason: string | null;
};

export type OAuthClient = {
  id:                   string;
  clientId:             string;
  name:                 string;
  type:                 string;
  authenticationScheme: string | null;
  disabled:             boolean;
  redirectUrls:         string[];
  createdBy:            string | null;
  creatorEmail:         string | null;
  createdAt:            string;
  updatedAt:            string;
};

// Returned ONCE on /oauth2/register — `client_secret` is the only chance to
// see the plain secret; subsequent reads will only show client_id.
export type OAuthClientCreated = {
  client_id:                  string;
  client_secret:              string;
  client_id_issued_at:        number;
  client_secret_expires_at:   number;
  redirect_uris:              string[];
  client_name:                string;
  grant_types:                string[];
  response_types:             string[];
  token_endpoint_auth_method: string;
};

// 16-hex BCM2712 OTP serial → "ebce-268d-1898-6556" for display. Falls
// back to the raw string for anything that's not the expected 16 hex
// chars (e.g. legacy "unknown" rows from before serial-aware pairing).
export function formatSerial(s: string | null | undefined): string {
  if (!s) return "";
  if (!/^[0-9a-fA-F]{16}$/.test(s)) return s;
  return `${s.slice(0, 4)}-${s.slice(4, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}`.toLowerCase();
}

// Domain enums mirrored from device-side StateDefines so display strings
// don't drift.
export const TEST_TYPE_LABELS = [
  "UNKNOWN", "AS1851", "CCD", "CPD", "BIR", "CRAMP", "ECAP",
] as const;

// Matches StateDefines::TestResult in the device repo (common/statedefines.h).
// Keep in sync — adding a value device-side requires a label here.
export const TEST_RESULT_LABELS = [
  "UNKNOWN",          // 0  TEST_RESULT_NONE
  "Pass",             // 1  TEST_RESULT_SUCCESS
  "Fail (NREG)",      // 2  TEST_RESULT_FAILED_REGULATION
  "Fail (OverV)",     // 3  TEST_RESULT_FAILED_OVERVOLTAGE
  "Fail (LowV)",      // 4  TEST_RESULT_FAILED_LOWVOLTAGE
  "Cancelled",        // 5  TEST_RESULT_FAILED_USER_INTERRUPT
  "Fail (CURR)",      // 6  TEST_RESULT_FAILED_CURRENT_FEEDBACK
  "Fail (INPV)",      // 7  TEST_RESULT_FAILED_INPUT_VOLTAGE
  "Fail (OverTemp)",  // 8  TEST_RESULT_FAILED_OVERTEMP
  "Fail (E-Stop)",    // 9  TEST_RESULT_FAILED_ESTOP
  "Cable unplugged",  // 10 TEST_RESULT_FAILED_CABLE_UNPLUGGED
  "Cable amp limit",  // 11 TEST_RESULT_FAILED_CURRENT_EXCEEDS_CABLE
  "Both cables in",   // 12 TEST_RESULT_FAILED_BOTH_CABLES_PLUGGED
  "Sensor fault",     // 13 TEST_RESULT_FAILED_SENSOR_FAULT
  "Fuse blown",       // 14 TEST_RESULT_FAILED_FUSE_BLOWN
  "Peer link lost",   // 15 TEST_RESULT_FAILED_PEER_LOST
  "Peer fault",       // 16 TEST_RESULT_FAILED_PEER_FAULT
  "Not calibrated",   // 17 TEST_RESULT_FAILED_NOT_CALIBRATED
  "Reverse polarity", // 18 TEST_RESULT_FAILED_REVERSE_POLARITY
  "No battery sense", // 19 TEST_RESULT_FAILED_NO_BATTERY_SENSE
  "Fail (WDG)",       // 20 TEST_RESULT_FAILED_WATCHDOG — deadman cut (main-loop stall)
  "Fail (Capacity)",  // 21 TEST_RESULT_FAILED_CAPACITY — delivered below the pass threshold
] as const;

// Shared duration formatter (header card, tests list, parameter table). Long
// capacity runs (15-48 h) need the hours; a raw seconds count read "55146 s".
export function fmtDurationMs(durationMs: number): string {
  const total = Math.round(durationMs / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
    : `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

export function testTypeLabel(t: number): string {
  return TEST_TYPE_LABELS[t] ?? `type-${t}`;
}

export function testResultLabel(r: number): string {
  return TEST_RESULT_LABELS[r] ?? `result-${r}`;
}

export function testResultPillClass(r: number): string {
  if (r === 1) return "pill pass";       // SUCCESS
  if (r === 5) return "pill cancelled";  // USER_INTERRUPT
  return "pill fail";                    // every other non-zero result
}

export function warrantyPillClass(state: string): string {
  if (state === "in_warranty") return "pill pass";
  if (state === "not_started") return "pill cancelled";
  return "pill fail"; // expired | void
}

export function warrantyLabel(state: string): string {
  return state === "in_warranty" ? "In warranty"
    : state === "expired" ? "Out of warranty"
    : state === "void" ? "Warranty void"
    : "Warranty not started";
}

// ---- Uptick integration (uptick_plan.md v0.1) --------------------------------

// v1 probe stored plain booleans (status-based); v2 stores
// {readable, count} — Uptick filters rows rather than 403ing, so the
// count is the actual permission signal. Old rows may still carry v1.
export type UptickProbeEntry =
  | boolean
  | null
  | { readable: boolean | null; count: number | null;
      canCreate?: boolean | null; canUpdate?: boolean | null };
export type UptickProbe = {
  tasks: UptickProbeEntry;
  assets: UptickProbeEntry;
  properties: UptickProbeEntry;
  clients: UptickProbeEntry;
};

// §14 discovery-by-convention scan (refreshed on Connect / Test connection).
export type UptickBt1851Question = {
  id: number; ref: string; type: string; label: string;
  section_id: number | null; set_id: number | null; set_name: string | null;
};
export type UptickDiscovery = {
  scanned_at: string;
  questions: UptickBt1851Question[];
  sets: Array<{ id: number; name: string }>;
  asset_types: Array<{ id: number; name: string }>;
  asset_type_names: Record<string, string>;
  types_checked: number;
  types_total: number | null;
  truncated: boolean;
  errors: string[];
};
export type UptickAttachTo = "task" | "asset" | "both" | "none";
export type UptickSettings = { attach_to: UptickAttachTo; auto_push: boolean };

export type UptickStatus =
  | { connected: false }
  | {
      connected: true;
      provider: string;
      tenant_subdomain: string;
      client_id: string;
      service_username: string;
      scopes: string | null;
      mode: "standard" | "minimal" | "insufficient" | null;
      probe: UptickProbe | null;
      mapping: { entries?: UptickMappingEntry[] } | null;
      settings: UptickSettings;
      discovery: UptickDiscovery | null;
      token_expires_at: string | null;
      enabled: boolean;
      last_error: string | null;
      last_error_at: string | null;
      connected_at: string;
      updated_at: string;
    };

export const uptickApi = {
  status: (orgId: string) =>
    http<UptickStatus>(`/v1/orgs/${orgId}/integrations/uptick`),

  connect: (orgId: string, body: {
    tenant_subdomain: string;
    client_id: string;
    client_secret: string;
    service_username: string;
    service_password: string;
  }) =>
    http<UptickStatus>(`/v1/orgs/${orgId}/integrations/uptick/connect`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  test: (orgId: string) =>
    http<UptickStatus>(`/v1/orgs/${orgId}/integrations/uptick/test`, {
      method: "POST",
    }),

  disconnect: (orgId: string) =>
    http<{ status: "ok"; connected: false }>(
      `/v1/orgs/${orgId}/integrations/uptick`,
      { method: "DELETE" },
    ),

  saveSettings: (orgId: string, settings: Partial<UptickSettings>) =>
    http<UptickStatus>(`/v1/orgs/${orgId}/integrations/uptick/settings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    }),
};

// ---- Uptick v0.2 / v0.2.1: mapping + push -----------------------------------

export type UptickQuestion = { id: number; type: string; label: string; ref?: string };
export type UptickMappingEntry = { question_id: number; template: unknown };
export type UptickPropertyHit = { propertyId: number; label: string | null };
export type UptickAssetHit = {
  // ref = the A-number (Uptick's uptick_ref); label = location · label.
  assetId: number; ref: string | null; label: string | null;
  typeName?: string | null;
};
export type UptickServicetaskHit = {
  servicetaskId: number; taskId: number | null;
  result: string | null; performedDate: string | null;
};
export type UptickDocOutcome = {
  ok: boolean; key?: string; existed?: boolean; error?: string; skipped?: boolean;
};
export type UptickJobDetail = {
  mode?: "answers" | "verdict-only";
  answers?: number; answers_updated?: number; answergroup_id?: number | null;
  questions?: Array<{ id: number; ref: string }>;
  verdict?: string; verdict_ok?: boolean;
  doc_task?: UptickDocOutcome; doc_asset?: UptickDocOutcome;
  task_id?: number | null; asset_id?: number | null;
  warnings?: string[];
  // A technician's matching tick was kept; only the readings and report added.
  verdict_kept?: boolean;
  // needs_confirmation: the result already on the service task, what this test
  // measured, and who wrote the existing one (by_test_id null = entered in Uptick).
  conflict?: {
    result: string; this_verdict: string; performed_date: string | null;
    by_test_id: string | null; by_test_name: string | null; by_test_started_at: string | null;
  };
};
// Push rule 2 (uptick_plan.md §12): a waiting result named with an A-number gets a suggested work order from the
// org's job list — for a person to confirm, never sent on its own.
export type UptickSuggestedJob = {
  servicetask_id: number; task_ref: string | null; due: string | null;
  site: string | null; address: string | null; asset_ref: string | null; location: string | null;
  ticked?: string | null; performed_date?: string | null;   // the line was already ticked in Uptick (P/F) and when
};
export type UptickSuggestion =
  | ({ kind: "match"; warnings: string[] } & UptickSuggestedJob)
  | { kind: "several"; options: UptickSuggestedJob[] }
  | { kind: "none"; ref: string }
  | { kind: "unlinked"; battery_id: string }      // battery ID not linked to an Uptick battery set yet
  | { kind: "collision"; battery_id: string };    // a "new" ID that already existed in the org

// Battery ID (uptick_plan.md §12): a battery set's own identity, B-XXXX-XXXX, and its Uptick link.
export type BatteryInfo = {
  battery_id: string;
  uptick_asset_id: number | null; linked_at: string | null; linked_by: string | null;
  collision_test_id: string | null;
  tests: Array<{ id: string; name: string | null; started_at: string; test_type: number; test_result: number }>;
};
export const batteryApi = {
  get: (orgId: string, batteryId: string) =>
    http<BatteryInfo>(`/v1/orgs/${orgId}/batteries/${encodeURIComponent(batteryId)}`),
  link: (orgId: string, batteryId: string, assetId: number | null) =>
    http<{ status: "ok"; battery_id: string; uptick_asset_id: number | null }>(
      `/v1/orgs/${orgId}/batteries/${encodeURIComponent(batteryId)}/link`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ asset_id: assetId }),
      }),
};
export type UptickJob = {
  test_id: string; status: "pending" | "done" | "failed" | "partial" | "unmatched" | "needs_confirmation";
  suggestion?: UptickSuggestion | null;
  servicetask_id: number | null; attempts: number;
  last_error: string | null; completed_at: string | null;
  detail?: UptickJobDetail;
  test_name?: string | null; started_at?: string | null;
};
export type UptickPushResult = {
  status: "ok" | "partial";
  servicetask_id: number; task_id: number | null;
  answergroup_id: number | null; answers: number; answers_updated: number;
  verdict: string; verdict_kept: boolean; mode: "answers" | "verdict-only";
  documents: { task: UptickDocOutcome; asset: UptickDocOutcome };
  warnings: string[];
};

export const uptickPushApi = {
  questions: (orgId: string) =>
    http<{ questions: UptickQuestion[] }>(`/v1/orgs/${orgId}/integrations/uptick/promptquestions`),
  saveMapping: (orgId: string, entries: UptickMappingEntry[]) =>
    http<{ status: "ok"; entries: number }>(`/v1/orgs/${orgId}/integrations/uptick/mapping`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entries }),
    }),
  searchProperties: (orgId: string, q: string) =>
    http<{ properties: UptickPropertyHit[]; cachedTotal: number | null }>(
      `/v1/orgs/${orgId}/integrations/uptick/servicetasks?q=${encodeURIComponent(q)}`),
  propertyAssets: (orgId: string, propId: number, all = false) =>
    http<{ assets: UptickAssetHit[]; filtered: "bt1851" | "name" | "none"; typeCount: number }>(
      `/v1/orgs/${orgId}/integrations/uptick/servicetasks?prop=${propId}${all ? "&all=1" : ""}`),
  assetServicetasks: (orgId: string, assetId: number) =>
    http<{ servicetasks: UptickServicetaskHit[] }>(
      `/v1/orgs/${orgId}/integrations/uptick/servicetasks?asset=${assetId}`),
  push: (orgId: string, testId: string, servicetaskId: number, opts?: { documentsOnly?: boolean; confirmOverwrite?: boolean }) =>
    http<UptickPushResult>(
      `/v1/orgs/${orgId}/integrations/uptick/push`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          test_id: testId, servicetask_id: servicetaskId,
          ...(opts?.documentsOnly ? { documents_only: true } : {}),
          ...(opts?.confirmOverwrite ? { confirm_overwrite: true } : {}),
        }),
      }),
  jobForTest: (orgId: string, testId: string) =>
    http<{ jobs: UptickJob[] }>(`/v1/orgs/${orgId}/integrations/uptick/jobs?test=${encodeURIComponent(testId)}`),
  jobs: (orgId: string, statuses?: UptickJob["status"][]) =>
    http<{ jobs: UptickJob[] }>(
      `/v1/orgs/${orgId}/integrations/uptick/jobs${statuses?.length ? `?status=${statuses.join(",")}` : ""}`),
};
